import React from 'react';

import { DeleteIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { DeleteListsForm } from '@vlabs/monokit/entities/lists';
import type { ButtonWithPermissionsProps } from '@vlabs/monokit/shared/ui/button-with-permissions';
import { ButtonWithPermissions } from '@vlabs/monokit/shared/ui/button-with-permissions';

import { useDeleteLists } from './useDeleteLists';
import { buildTestid } from '../../../shared/lib/utils';
import type { DeleteListsProps } from '../types';

export interface DeleteListsButtonProps extends
  DeleteListsProps,
  Omit<ButtonWithPermissionsProps, 'onClick' | 'permissions'> { }

const displayName = 'DeleteListsButton';
const testid = buildTestid(displayName);

function DeleteListsButton({
  listIds,
  onDeleteLists = undefined,
  children = undefined,
  permissions = undefined,
  'data-testid': dataTestId = 'DeleteListsButton',
  ...props
}: DeleteListsButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal();
  const deleteLists = useDeleteLists();

  return (
    <React.Fragment>
      {modal.wrap(
        <DeleteListsForm
          listIds={listIds}
          onCancel={modal.close}
          onSubmit={async (values) => {
            await deleteLists.mutateAsync(values);
            onDeleteLists?.();
            modal.close();
          }}
        />,
      )}

      <ButtonWithPermissions
        icon={<DeleteIcon />}
        kind="negative"
        onClick={modal.open}
        permissions={permissions}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {children !== undefined ? children : t('features:delete_lists.button')}
      </ButtonWithPermissions>
    </React.Fragment>
  );
}

DeleteListsButton.displayName = displayName;

export { DeleteListsButton };
